import {
  IonButton,
  IonCardContent,
  IonCol,
  IonGrid,
  IonIcon,
  IonInput,
  IonItem,
  IonList,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonText,
  IonTitle,
} from "@ionic/react";
import dayjs from "dayjs";
import {
  checkmarkOutline,
  closeOutline,
  createOutline,
  logoWhatsapp,
  personAddOutline,
  searchOutline,
} from "ionicons/icons";
import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useSession } from "../../hooks/SessionContext";
import { Paciente } from "../../interfaces";
import RegistrarPaciente from "../../pages/RegistrarPaciente/RegistrarPaciente";
import StyledButton from "../StyledButton/StyledButton";

import { APP_CONFIG, COLORS } from "../../constants/app";
import http from "../../hooks/httpAdapter";
import { dateHelpers } from "../../utils/dateHelpers";
import { transformers } from "../../utils/transformers";
require("dayjs/locale/es");

interface ContainerProps {
  recargarPagina: boolean;
  mostrarBuscarOtro: boolean;
}
const FichaPaciente: React.FC<ContainerProps> = ({
  recargarPagina,
  mostrarBuscarOtro,
}) => {
  const [buscarPor, setBuscarPor] = useState<"Nombre" | "Documento" | "HC">(
    "Nombre",
  );
  const [searchText, setSearchText] = useState("");
  const [mostrarRegistrar, setMostrarRegistrar] = useState<boolean>(false);
  const [pacientesCoinciden, setPacientesCoinciden] = useState<Paciente[]>([]);

  // Estados para la edición del celular
  const [editandoCelular, setEditandoCelular] = useState<boolean>(false);
  const [celularTemp, setCelularTemp] = useState<string>("");
  const [guardandoCelular, setGuardandoCelular] = useState<boolean>(false);

  const {
    hcUL,
    cargandoSession,
    pacienteCargado,
    setPacienteCargado,
    turnoSeleccionado,
    setTurnoSeleccionado,
    setSesionInvalida,
    mostrarNotificacion,
  } = useSession();
  const location = useLocation();
  const history = useHistory();

  const BlanquearPaciente = () => {
    // No limpiar turno seleccionado si estamos en DarTurno con un turno válido
    const deberiaPreservarTurno =
      location.pathname === "/portal/DarTurno" &&
      turnoSeleccionado &&
      turnoSeleccionado.codigo > 0;

    if (!deberiaPreservarTurno) {
      setTurnoSeleccionado(null);
    }
    setPacienteCargado(null);
    setSearchText("");
    setBuscarPor("Nombre");
  };

  const handleClickSeleccionarPaciente = (pacienteCargado: Paciente) => {
    setPacienteCargado(pacienteCargado);
  };

  const buscarPacientes = async (buscaHC: boolean) => {
    try {
      const config = { headers: { Authorization: `Bearer ${hcUL?.token}` } };
      let response;
      if (searchText !== "") {
        if (buscaHC) {
          response = await http()
            .get(`pacientes/?documento=${searchText}`, config)
            .catch((e) => {
              if (e.response && e.response.status === 401) {
                setSesionInvalida(true);
                // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
              } else {
                console.error(e);
              }
            });
        } else {
          response = await http()
            .get(`pacientes/?nombre=${searchText}`, config)
            .catch((e) => {
              if (e.response && e.response.status === 401) {
                setSesionInvalida(true);
                // history.push("/", { motivoRedireccion: "401" }); // Redirección en caso de error 401
              } else {
                console.error(e);
              }
            });
        }
        if (response.length !== 0) {
          if (response.length === 1) {
            handleClickSeleccionarPaciente(response[0]);
          } else {
            setPacientesCoinciden(response);
          }
        } else {
          mostrarNotificacion(
            true,
            "No se encontraron coincidencias",
            "amarillo",
          );
        }
      }
    } catch (e) {
      //@ts-ignore
      mostrarNotificacion(true, "Error: " + e.message, "rojo");
    }
  };

  useEffect(() => {
    if (cargandoSession === false) {
      if (pacienteCargado === null || pacienteCargado.codigo === 0) {
        BlanquearPaciente();
      } else {
        handleClickSeleccionarPaciente(pacienteCargado);
      }
    }
  }, [pacienteCargado, cargandoSession]);

  const handleClickBuscar = () => {
    if (searchText.length >= APP_CONFIG.SEARCH_MIN_CHARS) {
      switch (buscarPor) {
        case "HC":
        case "Documento":
          buscarPacientes(true);
          break;
        case "Nombre":
          buscarPacientes(false);
          break;
        default:
          mostrarNotificacion(true, "Selección inválida", COLORS.ERROR);
          break;
      }
    } else {
      mostrarNotificacion(
        true,
        `Debe ingresar al menos ${APP_CONFIG.SEARCH_MIN_CHARS} caracteres`,
        COLORS.WARNING,
      );
    }
  };
  //@ts-ignore
  const handleKeyPress = (key) => {
    if (key.key === "Enter" || key.keyCode === 13) {
      handleClickBuscar();
    }
  };

  // Funciones para editar el celular
  const iniciarEdicionCelular = () => {
    setCelularTemp(pacienteCargado?.celular || "");
    setEditandoCelular(true);
  };

  const cancelarEdicionCelular = () => {
    setEditandoCelular(false);
    setCelularTemp("");
  };

  const guardarCelular = async () => {
    if (!pacienteCargado) return;

    setGuardandoCelular(true);
    try {
      const config = { headers: { Authorization: `Bearer ${hcUL?.token}` } };

      // Intentar actualizar el paciente - IMPORTANTE: Verificar que este endpoint existe
      const response = await http()
        .put(
          `pacientes/${pacienteCargado.codigo}`,
          {
            ...pacienteCargado,
            celular: celularTemp,
          },
          config,
        )
        .catch((e) => {
          if (e.response && e.response.status === 401) {
            setSesionInvalida(true);
          } else if (e.response && e.response.status === 404) {
            // Si no existe endpoint PUT, podrías intentar POST para actualizar
            throw new Error("Endpoint de actualización no disponible");
          } else {
            console.error(e);
            throw e;
          }
        });

      // Actualizar el paciente cargado con el nuevo celular
      const pacienteActualizado = { ...pacienteCargado, celular: celularTemp };
      setPacienteCargado(pacienteActualizado);

      setEditandoCelular(false);
      setCelularTemp("");
      mostrarNotificacion(
        true,
        "Celular actualizado correctamente",
        COLORS.SUCCESS,
      );
    } catch (error: any) {
      console.error("Error actualizando celular:", error);
      mostrarNotificacion(
        true,
        error.message || "Error al actualizar el celular",
        COLORS.ERROR,
      );
    } finally {
      setGuardandoCelular(false);
    }
  };

  const handleCelularUpdate = (nuevoCelular: string) => {
    if (!pacienteCargado) return;
    const pacienteActualizado = { ...pacienteCargado, celular: nuevoCelular };
    setPacienteCargado(pacienteActualizado);
  };

  // Funciones para WhatsApp
  const formatearNumeroWhatsApp = (numero: string): string => {
    return transformers.toWhatsAppNumber(numero);
  };

  const abrirWhatsApp = (numero: string) => {
    if (!numero || numero === "-") {
      mostrarNotificacion(
        true,
        "No hay número de celular disponible",
        COLORS.WARNING,
      );
      return;
    }

    const numeroFormateado = formatearNumeroWhatsApp(numero);
    // URL de WhatsApp sin mensaje prearmado
    const whatsappURL = `https://wa.me/${numeroFormateado}`;

    // Abrir en nueva ventana/pestaña
    window.open(whatsappURL, "_blank");
  };

  const renderPacientes = () => {
    return pacientesCoinciden.map((pacienteCargado) => (
      <IonItem
        className="fila ion-align-items-center"
        key={pacienteCargado.codigo}
        onClick={(e) => handleClickSeleccionarPaciente(pacienteCargado)}
      >
        <IonCol className="celda" size="3">
          <small>{pacienteCargado.hc}</small>
        </IonCol>
        <IonCol className="celda" size="3">
          <small>
            {pacienteCargado.apellido}, {pacienteCargado.nombre}
          </small>
        </IonCol>
        <IonCol className="celda" size="3">
          <small>{pacienteCargado.mutualNombre}</small>
        </IonCol>
        <IonCol className="celda" size="3">
          <small>
            {pacienteCargado.mutualAfiliado === null
              ? "-"
              : pacienteCargado.mutualAfiliado}
          </small>
        </IonCol>
      </IonItem>
    ));
  };

  return (
    <>
      {pacienteCargado !== null && pacienteCargado.codigo !== 0 ? (
        <IonCardContent>
          <IonRow className="ion-text-start">
            <IonCol className="ion-text-start" sizeMd="4" sizeXs="6">
              <IonText>
                <h2>
                  <strong>
                    {pacienteCargado.apellido}, {pacienteCargado.nombre}
                  </strong>
                </h2>
              </IonText>
            </IonCol>
            <IonCol className="ion-text-start" sizeMd="3" sizeXs="6">
              <IonText>Documento: {pacienteCargado.documentoNro}</IonText>
            </IonCol>
            <IonCol className="ion-text-start" sizeMd="2" sizeXs="6">
              <IonText>
                Edad:{" "}
                {pacienteCargado.nacimiento === null ||
                !pacienteCargado.nacimiento
                  ? "-"
                  : dateHelpers.calculateAge(pacienteCargado.nacimiento) +
                    " años"}
              </IonText>
            </IonCol>
            <IonCol className="ion-text-start" sizeMd="3" sizeXs="6">
              <IonText>
                Nacimiento:{" "}
                {pacienteCargado.nacimiento === null
                  ? "-"
                  : dayjs(pacienteCargado.nacimiento).format("DD/MM/YYYY")}
              </IonText>
            </IonCol>
          </IonRow>
          <IonRow></IonRow>
          <IonRow>
            <IonCol className="ion-text-start">
              <IonText>
                Mutual:{" "}
                {pacienteCargado.mutualNombre === null
                  ? "-"
                  : pacienteCargado.mutualNombre}
              </IonText>
            </IonCol>
            <IonCol className="ion-text-start">
              <IonText>
                Nro Afiliado:{" "}
                {pacienteCargado.mutualAfiliado === null
                  ? "-"
                  : pacienteCargado.mutualAfiliado}
              </IonText>
            </IonCol>
          </IonRow>
          <IonRow>
            <IonCol className="ion-text-start">
              <IonText>
                Correo:{" "}
                {pacienteCargado.email === null ? "-" : pacienteCargado.email}
              </IonText>
            </IonCol>
            <IonCol className="ion-text-start">
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                {editandoCelular ? (
                  <>
                    <IonText>Celular:</IonText>
                    <IonInput
                      value={celularTemp}
                      onIonInput={(e) => setCelularTemp(e.detail.value!)}
                      placeholder="Ingrese número de celular"
                      style={{
                        border: "1px solid var(--ion-color-step-850)",
                        borderRadius: "4px",
                        padding: "4px 8px",
                        minWidth: "150px",
                        fontSize: "14px",
                      }}
                      disabled={guardandoCelular}
                    />
                    <IonIcon
                      icon={checkmarkOutline}
                      style={{
                        color: "var(--ion-color-success)",
                        cursor: guardandoCelular ? "not-allowed" : "pointer",
                        fontSize: "22px",
                        opacity: guardandoCelular ? 0.5 : 1,
                        fontWeight: "bold",
                        title: "Guardar celular",
                      }}
                      onClick={guardandoCelular ? undefined : guardarCelular}
                    />
                    <IonIcon
                      icon={closeOutline}
                      style={{
                        color: "var(--ion-color-danger)",
                        cursor: guardandoCelular ? "not-allowed" : "pointer",
                        fontSize: "22px",
                        opacity: guardandoCelular ? 0.5 : 1,
                        fontWeight: "bold",
                        title: "Cancelar edición",
                      }}
                      onClick={
                        guardandoCelular ? undefined : cancelarEdicionCelular
                      }
                    />
                    {guardandoCelular && (
                      <IonText
                        style={{
                          fontSize: "12px",
                          color: "var(--ion-color-medium)",
                        }}
                      >
                        Guardando...
                      </IonText>
                    )}
                  </>
                ) : (
                  <>
                    <IonText>
                      Celular:{" "}
                      {pacienteCargado.celular === null ? (
                        "-"
                      ) : (
                        <span
                          style={{
                            color: "var(--ion-color-success)",
                            cursor: "pointer",
                            textDecoration: "underline",
                            fontWeight: "500",
                          }}
                          onClick={() =>
                            abrirWhatsApp(pacienteCargado.celular!)
                          }
                          title="Para que el acceso a WhatsApp sea funcional, se debe respetar el formato internacional del número del celular. En el caso de argentina siempre debe empezar con 549 + caracteristica + número (sin espacios, sin 0 de la característica, sin el 15 inicial, ni guiones o paréntesis.). Por ejemplo para Córdoba: 5493511234567. En caso que no se cumpla el formato requerido, deberá editar el número de celular desde el icono rojo de editar y recargar la página para que se actualice el formato."
                        >
                          {pacienteCargado.celular}
                        </span>
                      )}
                    </IonText>

                    {/* Botón de WhatsApp si hay celular */}
                    {pacienteCargado.celular &&
                      pacienteCargado.celular !== "-" && (
                        <IonIcon
                          icon={logoWhatsapp}
                          style={{
                            color: "var(--ion-color-success)",
                            cursor: "pointer",
                            fontSize: "22px",
                            marginLeft: "8px",
                          }}
                          onClick={() =>
                            abrirWhatsApp(pacienteCargado.celular!)
                          }
                          title={`Chatear por WhatsApp con ${pacienteCargado.nombre} ${pacienteCargado.apellido}`}
                        />
                      )}

                    {/* Botón de editar */}
                    <IonIcon
                      icon={createOutline}
                      style={{
                        color: "var(--ion-color-primary)",
                        cursor: "pointer",
                        fontSize: "22px",
                        marginLeft: "8px",
                      }}
                      onClick={iniciarEdicionCelular}
                      title="Editar celular"
                    />
                  </>
                )}
              </div>
            </IonCol>
          </IonRow>
          {mostrarBuscarOtro && (
            <IonRow>
              <IonCol>
                <IonButton
                  expand="block"
                  className="azul"
                  onClick={BlanquearPaciente}
                >
                  <IonIcon icon={searchOutline} />
                  <IonText>Buscar otro paciente ya registrado</IonText>
                </IonButton>
              </IonCol>
            </IonRow>
          )}
        </IonCardContent>
      ) : (
        <IonCardContent className="ion-padding">
          <IonGrid>
            <IonRow className="ion-align-items-center">
              <IonCol sizeXs="12" sizeMd="3">
                <IonSelect
                  label="Buscar paciente cargado segun "
                  labelPlacement="floating"
                  interface="popover"
                  value={buscarPor}
                  onIonChange={(e) => setBuscarPor(e.detail.value)}
                >
                  <IonSelectOption value="HC">HC</IonSelectOption>
                  <IonSelectOption value="Documento">Documento</IonSelectOption>
                  <IonSelectOption value="Nombre">
                    Nombre y/o apellido
                  </IonSelectOption>
                </IonSelect>
              </IonCol>
              <IonCol sizeXs="12" sizeMd="7">
                <IonInput
                  value={searchText}
                  placeholder={`Buscar por ${buscarPor}`}
                  onIonChange={(e) => setSearchText(e.detail.value!)}
                  onKeyUp={handleKeyPress}
                  autoFocus={true}
                />
              </IonCol>
              <IonCol sizeXs="12" sizeMd="2">
                <StyledButton
                  onClick={handleClickBuscar}
                  expand="block"
                  className="azul"
                >
                  <IonIcon icon={searchOutline} />
                  <IonText>Buscar</IonText>
                </StyledButton>
              </IonCol>
            </IonRow>
            <IonRow>
              <IonCol size="12">
                <StyledButton
                  onClick={() => setMostrarRegistrar(true)}
                  expand="block"
                  className="azul"
                >
                  <IonIcon icon={personAddOutline} />
                  <IonText>{"Agregar nuevo paciente"}</IonText>
                </StyledButton>
              </IonCol>
            </IonRow>
            <br />
            {pacientesCoinciden.length > 0 && (
              <IonList>
                <IonItem className="fila ion-align-items-center">
                  <IonCol className="celda" size="3">
                    <IonTitle>HC</IonTitle>
                  </IonCol>
                  <IonCol className="celda" size="3">
                    <IonTitle>Nombre</IonTitle>
                  </IonCol>
                  <IonCol className="celda" size="3">
                    <IonTitle>Mutual</IonTitle>
                  </IonCol>
                  <IonCol className="celda" size="3">
                    <IonTitle>Nro afiliado</IonTitle>
                  </IonCol>
                </IonItem>
                {renderPacientes()}
              </IonList>
            )}
          </IonGrid>
        </IonCardContent>
      )}

      <RegistrarPaciente
        abrirModal={mostrarRegistrar}
        setAbrirModal={setMostrarRegistrar}
        setPacienteRegistrado={handleClickSeleccionarPaciente}
      />
    </>
  );
};

export default FichaPaciente;
